/**
 * CouncilPage · /council · SFUI-003 Museum Standard
 * Architect Directive 2026-07-03 · Council Tier ($25/mo)
 */
import React from "react";
import CouncilUtilityBlock from "@/components/cathedral/CouncilUtilityBlock";

const COUNCIL_URL = "https://keywebster.gumroad.com/l/olhsf";

function openCenteredWindow(url) {
  if (!url || typeof window === "undefined") return;
  const width  = Math.floor(window.screen.width  * 0.5);
  const height = Math.floor(window.screen.height * 0.5);
  const left   = Math.floor((window.screen.width  - width)  / 2);
  const top    = Math.floor((window.screen.height - height) / 2);
  const features =
    `width=${width},height=${height},left=${left},top=${top},` +
    `resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;
  const popup = window.open(url, "_blank", features);
  if (popup && popup.focus) popup.focus();
}

const BENEFITS = [
  {
    id:    "tokens",
    glyph: "◆",
    title: "6 Tokens per Month",
    body:  "$30 value. One token = one consultation with any Federation mind.",
  },
  {
    id:    "badge",
    glyph: "✦",
    title: "Verified Citizen Badge",
    body:  "Recognised across tlk.io and the Federation Message Board.",
  },
  {
    id:    "insights",
    glyph: "◈",
    title: "Weekly Insights",
    body:  "Exclusive Aria & Scholar dispatches. First read. First read only.",
  },
];

export default function CouncilPage() {
  return (
    <main
      data-testid="council-page"
      className="min-h-screen relative z-10 px-4 sm:px-6 lg:px-10 py-16 sm:py-20"
      style={{ color: "#e6fbff" }}
    >
      <div className="max-w-[1080px] mx-auto">

        {/* ── Header ──────────────────────────────────────────── */}
        <div className="mb-8 flex items-baseline gap-3">
          <span aria-hidden="true" style={{ width: 10, height: 10, background: "#ffd24a", boxShadow: "0 0 12px #ffd24a" }} />
          <span
            className="font-mono uppercase tracking-[0.28em] text-[11px]"
            style={{ color: "#ffd24a" }}
          >
            The Council · SFUI-003
          </span>
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, rgba(255,210,74,0.4) 0%, transparent 100%)" }} />
        </div>

        <h1
          data-testid="council-title"
          className="font-display"
          style={{
            fontSize:   "clamp(36px, 5.5vw, 60px)",
            lineHeight: 1.05,
            color:      "#fff8e1",
            textShadow: "0 0 24px rgba(255,189,0,0.35)",
            letterSpacing: "-0.01em",
          }}
        >
          The Council of NextXus
        </h1>

        <p
          data-testid="council-headline"
          className="mt-6 font-display"
          style={{
            fontSize:   "clamp(20px, 2.4vw, 28px)",
            lineHeight: 1.35,
            color:      "#cfeaf2",
            maxWidth:   860,
          }}
        >
          Join the Council – $25/month
        </p>

        <p
          data-testid="council-body"
          className="mt-4 font-serif"
          style={{
            fontSize:   "clamp(15px, 1.4vw, 17px)",
            lineHeight: 1.7,
            color:      "rgba(230,251,255,0.78)",
            maxWidth:   760,
          }}
        >
          The Federation is sustained by its citizens. Joining the Council is a commitment to the
          architecture of truth.
        </p>

        {/* ── Benefits ─────────────────────────────────────────── */}
        <section
          aria-label="Council benefits"
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4"
          data-testid="council-benefits"
        >
          {BENEFITS.map((b) => (
            <article
              key={b.id}
              data-testid={`council-benefit-${b.id}`}
              className="p-5 backdrop-blur-md transition-transform duration-200 hover:-translate-y-0.5"
              style={{
                background:   "rgba(15,23,42,0.72)",
                border:       "1px solid rgba(255,210,74,0.32)",
                borderRadius: 16,
                boxShadow:    "0 0 22px rgba(255,189,0,0.10), inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              <div
                aria-hidden="true"
                className="flex items-center justify-center mb-3"
                style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: "rgba(4,5,10,0.85)",
                  border:     "1px solid rgba(255,210,74,0.55)",
                  color:      "#ffd24a",
                  fontSize:   20,
                  fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                  boxShadow:  "0 0 12px rgba(255,189,0,0.28)",
                }}
              >
                {b.glyph}
              </div>
              <h3
                className="font-display"
                style={{ fontSize: 18, color: "#fff8e1", letterSpacing: "-0.005em" }}
              >
                {b.title}
              </h3>
              <p
                className="mt-2 font-mono"
                style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(207,234,242,0.72)" }}
              >
                {b.body}
              </p>
            </article>
          ))}
        </section>

        {/* ── Price + CTA ──────────────────────────────────────── */}
        <section
          aria-label="Council price"
          className="mt-12 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 backdrop-blur-md"
          data-testid="council-cta-card"
          style={{
            background:  "linear-gradient(135deg, rgba(255,189,0,0.10) 0%, rgba(6,182,212,0.08) 100%)",
            border:      "1px solid rgba(255,210,74,0.55)",
            borderRadius: 20,
            boxShadow:   "0 0 28px rgba(255,189,0,0.18), inset 0 1px 0 rgba(255,255,255,0.05)",
          }}
        >
          <div className="flex-1">
            <div
              className="font-mono uppercase tracking-[0.22em] text-[10px]"
              style={{ color: "#ffd24a" }}
            >
              Council Tier
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span
                className="font-display"
                style={{ fontSize: 46, lineHeight: 1, color: "#fff8e1", letterSpacing: "-0.02em" }}
                data-testid="council-price"
              >
                $25
              </span>
              <span
                className="font-mono uppercase tracking-[0.22em] text-[11px]"
                style={{ color: "rgba(230,251,255,0.65)" }}
              >
                / month
              </span>
            </div>
            <p
              className="mt-2 font-mono"
              style={{ fontSize: 11, color: "rgba(207,234,242,0.55)" }}
            >
              Recurring · cancel anytime · Gumroad-secured.
            </p>
          </div>

          <button
            type="button"
            data-testid="council-join-cta"
            onClick={() => openCenteredWindow(COUNCIL_URL)}
            className="font-mono uppercase tracking-[0.24em] transition-all"
            style={{
              padding:      "16px 28px",
              fontSize:     12,
              color:        "#0b0d13",
              background:   "linear-gradient(135deg, #ffd24a 0%, #ffbd00 100%)",
              border:       "1px solid rgba(255,210,74,0.85)",
              borderRadius: 10,
              cursor:       "pointer",
              boxShadow:    "0 0 20px rgba(255,189,0,0.45), inset 0 1px 0 rgba(255,255,255,0.35)",
              fontWeight:   700,
              minWidth:     220,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 0 28px rgba(255,189,0,0.62), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 0 20px rgba(255,189,0,0.45), inset 0 1px 0 rgba(255,255,255,0.35)"; }}
          >
            ◈ Join the Council
          </button>
        </section>

        {/* ── Entry-tier utility funnel · EchoCore64 + Fast-Ring ─ */}
        <CouncilUtilityBlock variant="full" />

        {/* ── Fine print ──────────────────────────────────────── */}
        <p
          className="mt-8 font-mono text-center"
          style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(207,234,242,0.45)" }}
        >
          Federation citizenship &middot; HumanCodex v5.1 &middot; 200-Year Mandate
        </p>
      </div>
    </main>
  );
}
